import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { Grid } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import BookingData from "../BookingData/BookingData";
import TotalDatainfo from "../TotalDataInfo/TotalDatainfo";
import BreadcrumbComp from "../../CommonComponents/Breadcrumb/Breadcrumb";
import TodaysFollowups from "../Followup/TodaysFollowups";
import { RightDrawer, ModalHeader, ModalBody, ModalFooter } from "../../CommonComponents/RightDrawer";
import { Button } from "../../Inquiary/components/common/Button";
import { canAssignStaff, hasPermission } from "../../../utils/commonutils";
import {
  DASHBOARD_SECTIONS,
  KPI_META,
  defaultDashboardLayout,
  normalizeDashboardLayout,
  readStoredLayout,
  reorderList,
  writeStoredLayout,
} from "../../../utils/dashboardLayout";
import { getDashboardLayout, saveDashboardLayout } from "../../../services/dashboardService";

const SECTION_TITLES = DASHBOARD_SECTIONS.reduce((acc, item) => {
  acc[item.id] = item.title;
  return acc;
}, {});

const LayoutRow = ({ title, visible, isFirst, isLast, onMove, onToggle }) => (
  <div className={`layout-row ${visible ? "" : "is-off"}`}>
    <div>
      <div className="layout-row-title">{title}</div>
      <div className="layout-row-hint">{visible ? "Visible" : "Hidden"}</div>
    </div>
    <div className="layout-row-actions">
      <button type="button" className="layout-icon-btn" onClick={onToggle} title={visible ? "Hide" : "Show"}>
        {visible ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
      </button>
      <button type="button" className="layout-icon-btn" disabled={isFirst} onClick={() => onMove(-1)} title="Move up">
        <KeyboardArrowUpIcon fontSize="small" />
      </button>
      <button type="button" className="layout-icon-btn" disabled={isLast} onClick={() => onMove(1)} title="Move down">
        <KeyboardArrowDownIcon fontSize="small" />
      </button>
    </div>
  </div>
);

const DashboardComp = (props) => {
  const userId = props.authData?.idToken || props.authData?._id || "anon";
  const name = props.authData?.firstName || props.authData?.name || "there";
  const role = props.authData?.role || "staff";
  const [open, setOpen] = useState(false);
  const [layout, setLayout] = useState(() => readStoredLayout(userId));
  const saveTimer = useRef(null);
  const dirtyRef = useRef(false);

  const persist = (next) => {
    dirtyRef.current = true;
    const normalized = normalizeDashboardLayout(next);
    setLayout(normalized);
    writeStoredLayout(userId, normalized);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveDashboardLayout(props, normalized);
    }, 250);
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      const response = await getDashboardLayout(props);
      if (!active || dirtyRef.current || !response?.data) return;
      const normalized = normalizeDashboardLayout(response.data);
      setLayout(normalized);
      writeStoredLayout(userId, normalized);
    };
    load();
    return () => {
      active = false;
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [userId]);

  const canShowSection = (id) => {
    if (id === "tasks") return hasPermission("read_task");
    if (id === "followups") return hasPermission("read_followup") || hasPermission("read_inquiry");
    return true;
  };

  const sections = useMemo(
    () => (layout.sections || []).filter((section) => canShowSection(section.id)),
    [layout.sections]
  );
  const visibleSections = sections.filter((section) => section.visible !== false);

  const availableKpis = useMemo(() => {
    const isLead = canAssignStaff(role);
    const allowed = new Set();
    if (hasPermission("read_followup") || hasPermission("read_inquiry")) {
      allowed.add("followupToday");
      allowed.add("followupOverdue");
      allowed.add("followupUpcoming");
    }
    if (hasPermission("read_inquiry")) allowed.add("inquiries");
    if (hasPermission("read_task")) allowed.add("tasks");
    if (hasPermission("read_paymentrecovery")) allowed.add("payment");
    if (hasPermission("read_casemanagement")) allowed.add("cases");
    if (hasPermission("read_customers")) allowed.add("customers");
    if (hasPermission("read_communication")) allowed.add("communications");
    if (isLead && hasPermission("read_employee")) allowed.add("employees");
    if (isLead && hasPermission("read_role")) allowed.add("roles");
    return (layout.kpiOrder || []).filter((id) => allowed.has(id));
  }, [layout.kpiOrder, role]);

  const moveSection = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= sections.length) return;
    persist({
      ...layout,
      sections: reorderList(sections, index, nextIndex),
    });
  };

  const toggleSection = (id) => {
    persist({
      ...layout,
      sections: layout.sections.map((section) =>
        section.id === id ? { ...section, visible: section.visible === false } : section
      ),
    });
  };

  const moveKpi = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= availableKpis.length) return;
    const nextVisible = reorderList(availableKpis, index, nextIndex);
    const rest = (layout.kpiOrder || []).filter((id) => !nextVisible.includes(id));
    persist({ ...layout, kpiOrder: [...nextVisible, ...rest] });
  };

  const toggleKpi = (id) => {
    const hidden = new Set(layout.hiddenKpis || []);
    if (hidden.has(id)) hidden.delete(id);
    else hidden.add(id);
    persist({ ...layout, hiddenKpis: Array.from(hidden) });
  };

  const renderSection = (id) => {
    if (id === "kpis") {
      return <TotalDatainfo kpiOrder={layout.kpiOrder} hiddenKpis={layout.hiddenKpis} />;
    }
    if (id === "tasks") return <BookingData />;
    if (id === "followups") return <TodaysFollowups />;
    return null;
  };

  return (
    <div>
      <Grid container>
        <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
          <BreadcrumbComp items={[{ name: "Dashboard", active: true, link: "" }]} />
        </Grid>
        <Grid item xs={12}>
          <div className="dashboard-hero">
            <div>
              <div className="page-kicker">Overview</div>
              <div className="dashboard-hello">Hi {name}</div>
              <div className="dashboard-role">{role} · Your workspace</div>
            </div>
            <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
              Arrange dashboard
            </Button>
          </div>
        </Grid>
        <Grid item xs={12}>
          <div className="dashboard-stack">
            {visibleSections.map((section) => (
              <div key={section.id} className="dash-section">
                {renderSection(section.id)}
              </div>
            ))}
          </div>
        </Grid>
      </Grid>

      <RightDrawer isOpen={open} onClose={() => setOpen(false)} size="md">
        <ModalHeader kicker="Dashboard" toggle={() => setOpen(false)}>
          Arrange layout
        </ModalHeader>
        <ModalBody>
          <p className="layout-help">
            Use the arrows to move a block. Use the eye to hide it. Changes apply straight away.
          </p>
          <div className="layout-group-title">Sections</div>
          {sections.map((section, index) => (
            <LayoutRow
              key={section.id}
              title={SECTION_TITLES[section.id] || section.id}
              visible={section.visible !== false}
              isFirst={index === 0}
              isLast={index === sections.length - 1}
              onMove={(direction) => moveSection(index, direction)}
              onToggle={() => toggleSection(section.id)}
            />
          ))}
          <div className="layout-group-title">Summary cards</div>
          {availableKpis.map((id, index) => (
            <LayoutRow
              key={id}
              title={KPI_META.find((item) => item.id === id)?.label || id}
              visible={!layout.hiddenKpis?.includes(id)}
              isFirst={index === 0}
              isLast={index === availableKpis.length - 1}
              onMove={(direction) => moveKpi(index, direction)}
              onToggle={() => toggleKpi(id)}
            />
          ))}
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={() => persist(defaultDashboardLayout())}>
            Reset
          </Button>
          <Button onClick={() => setOpen(false)}>Done</Button>
        </ModalFooter>
      </RightDrawer>
    </div>
  );
};

const mapStateToProps = (state) => ({
  authData: state.authReducer,
});

export default withRouter(connect(mapStateToProps)(DashboardComp));
